import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/client'
import AddIcon from '@mui/icons-material/Add'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { SelectChangeEvent } from '@mui/material'
import {
  GridCellValue,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid'

import Avatar from 'components/Common/Avatar'
import Box from 'components/Common/Box'
import Button from 'components/Common/Button'
import Table from 'components/Common/Table'
import Tooltip from 'components/Common/Table/Tooltip'
import BiggerFontTooltip from 'components/Common/Table/Tooltip/BiggerFontTooltip'
import { TabObjects } from 'components/Common/Tabs'
import Title from 'components/Common/Title'
import { Filter } from 'components/Inspection/Dashboard/FilterCard'
import DateRangeFilter from 'components/Inspection/Listing/DateFilter'
import FieldFilter from 'components/Inspection/Listing/FieldFilter'
import CarTrimPopover from 'components/Inventory/Common/CarTrimPopover'
import CertifiedTag from 'components/Inventory/Common/CertifiedTag'
import FilterByCriteria from 'components/Listing/FilterByCriteria'

import { cypressAddButton } from 'constants/cypress'
import { FilteringOption } from 'constants/Inspection/filterByCriteria'
import { InventoryColumnField, unpublishedHash } from 'constants/inventory'
import { inventoryStaticFields } from 'constants/Inventory/listing'
import { routes } from 'constants/routes'
import {
  commonListColumns,
  defaultStatusSortModel,
  selectItems,
} from 'constants/table'
import { textFiles } from 'constants/textFiles'
import { UrlParamNames } from 'constants/urlQuery'
import useLocale from 'hooks/useLocale'
import useNotification from 'hooks/useNotification'
import useQueryState from 'hooks/useQueryState'
import useSetting from 'hooks/useSetting'
import useTranslation from 'hooks/useTranslation'
import useUser from 'hooks/useUser'
import { CurboSpot } from 'models/curboSpot'
import { CalendarRangeType } from 'models/date'
import { UserRoles } from 'models/role'
import {
  FilterInputVariable,
  GenericData,
  ListingFilterType,
} from 'models/services/base'
import {
  CarProviderEnum,
  ListInventory,
} from 'models/services/inventory/listing'
import { ExtendedStatus } from 'models/services/status'
import { InventoryStatusMap } from 'models/status'
import { getIsoDate } from 'utils/date'
import {
  checkSearchEmptiness,
  cleanFilters,
  generateFilterInput,
} from 'utils/filters'
import {
  addCommas,
  createFieldSelectItems,
  createFieldSelectLabels,
  defaultFields,
  formatFiltersWithCertification,
  formatInventories,
} from 'utils/Inventory/listing'
import { buildDetailRoute } from 'utils/routes'
import {
  serializeFields,
  serializeFilters,
  serializePage,
  serializePageSize,
  serializeRange,
  serializeSearch,
  serializeSortModel,
} from 'utils/serializers'
import { verifyParam } from 'utils/verifyUrlData'

import {
  GET_DEALERS,
  GET_PUBLISHED_CURBO_SPOTS,
} from 'graphQL/Common/Dealer/queries'
import {
  GET_CAR_STATUSES,
  LIST_INVENTORY,
} from 'graphQL/Inventory/Listing/queries'
import { GET_BRANDS } from 'graphQL/Operations/Model/Creation/queries'
import {
  GET_BODY_STYLES,
  GET_CATEGORIES,
  GET_COLORS,
  GET_COUNTRY_VERSIONS,
  GET_DRIVE_TRAINS,
  GET_FEATURES,
  GET_FUEL_TYPES,
  GET_MODELS,
  GET_TRANSMISSIONS,
} from 'graphQL/Operations/Trim/Detail/queries'

import { StyledTextField } from 'styles/inspection/listing'

const InventoryListingPage = () => {
  // Left supervisor validations commented for future edit and delete functionalities
  const { validateAllowedRoles } = useUser()
  const supervisor = validateAllowedRoles([
    UserRoles.ADMIN,
    UserRoles.INVENTORY_SUPERVISOR,
  ])
  const admin = validateAllowedRoles([UserRoles.ADMIN])

  const { show } = useNotification()
  const [selectedLanguage] = useLocale()
  const appSetting = useSetting()[2]
  const currency = appSetting ? appSetting.currency : null
  const priceCurrency = currency ? `${currency.code}` : ''
  const history = useHistory()
  const { text } = useTranslation(textFiles.INVENTORY_LISTING)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const location = useLocation()
  const { search, hash } = location

  const [tabValue, setTabValue] = useState<string | number>(
    location && location.hash === unpublishedHash ? 1 : 0
  )
  const defaultSortedFields = [...defaultFields].sort()

  const [selectedFields, setSelectedFields] = useQueryState<string[]>(
    UrlParamNames.FIELDS,
    (verifyParam(UrlParamNames.FIELDS, search) as string[]) ||
      defaultSortedFields,
    serializeFields,
    defaultSortedFields
  )

  const [filtersList, setFiltersList] = useQueryState<Filter[]>(
    UrlParamNames.FILTERS,
    (verifyParam(UrlParamNames.FILTERS, search) as Filter[]) || [],
    serializeFilters
  )

  const [dateRange, setDateRange] = useQueryState<CalendarRangeType>(
    UrlParamNames.DATE,
    (verifyParam(UrlParamNames.DATE, search) as CalendarRangeType) || [],
    serializeRange
  )

  const [searchValue, setSearchValue] = useQueryState<string | undefined>(
    UrlParamNames.SEARCH,
    verifyParam(UrlParamNames.SEARCH, search) as string,
    serializeSearch
  )

  const [pageSize, setPageSize] = useQueryState<number>(
    UrlParamNames.LIMIT,
    (verifyParam(UrlParamNames.LIMIT, search) as number) || 10,
    serializePageSize,
    10
  )
  const [page, setPage] = useQueryState<number>(
    UrlParamNames.PAGE,
    (verifyParam(UrlParamNames.PAGE, search) as number) || 1,
    serializePage
  )

  const [sortModel, setSortModel] = useQueryState<GridSortModel>(
    UrlParamNames.SORT,
    (verifyParam(UrlParamNames.SORT, search) as GridSortModel) ||
      defaultStatusSortModel,
    serializeSortModel,
    defaultStatusSortModel
  )
  const [filterInput, setFiltersInput] = useState<ListingFilterType>(
    generateFilterInput(filtersList)
  )
  const [searchInput, setSearchInput] = useState<string>(searchValue || '')

  const [inventoryQuantity, setInventoryQuantity] = useState<number>(0)
  const [inventoryData, setInventoryData] = useState<GridRowsProp>([])
  const [deleteItem, setDeleteItem] = useState<GridRowId | null>(null)
  const [curboSpots, setCurboSpots] = useState<CurboSpot[]>([])
  const [statusList, setStatusList] = useState<ExtendedStatus[]>([])
  const [colorsList, setColorsList] = useState<FilteringOption[]>([])
  const [modelsList, setModelsList] = useState<FilteringOption[]>([])
  const [brandsList, setBrandsList] = useState<FilteringOption[]>([])
  const [bodyStyleList, setBodyStyleList] = useState<FilteringOption[]>([])
  const [countryVersionList, setCountryVersionList] = useState<
    FilteringOption[]
  >([])
  const [fuelTypeList, setFuelTypeList] = useState<FilteringOption[]>([])
  const [transmissionList, setTransmissionList] = useState<FilteringOption[]>(
    []
  )
  const [driveTrainList, setDriveTrainList] = useState<FilteringOption[]>([])
  const [dealerList, setDealerList] = useState<FilteringOption[]>([])
  const [categoryList, setCategoryList] = useState<FilteringOption[]>([])
  const [featuresList, setFeaturesList] = useState<FilteringOption[]>([])

  const { fromDate, toDate } = useMemo(() => {
    return {
      fromDate: getIsoDate(dateRange.fromDate),
      toDate: getIsoDate(dateRange.toDate),
    }
  }, [dateRange])

  const providersList: FilteringOption[] = Object.values(CarProviderEnum)
    .filter((providerType) => typeof providerType !== 'number')
    .map((providerType) => ({
      name: text.providers[providerType],
      value: String(providerType).replace('_', '-').toLowerCase(),
      disabled: false,
    }))

  const getStatusesIn =
    tabValue === 0 ? ['available', 'coming-soon', 'sold'] : ['not-available']

  const getStatusesNin =
    tabValue === 0 ? ['not-available'] : ['available', 'coming-soon', 'sold']

  const tabs: TabObjects[] = [
    {
      id: 0,
      item: {
        label: text.publishedTab,
        value: 0,
        badge: 0,
      },
    },
    {
      id: 1,
      item: {
        label: text.notPublishedTab,
        value: 1,
        badge: 0,
      },
    },
  ]

  const vehicleTypes: FilteringOption[] = [
    {
      name: generalText.status.certified,
      value: 'certified',
      disabled: false,
    },
    {
      name: generalText.status.notCertified,
      value: 'notCertified',
      disabled: false,
    },
  ]

  const fieldSelectItems = supervisor
    ? createFieldSelectItems(text.fieldSelect)
    : createFieldSelectItems(text.fieldSelect).filter(
        (item) => item.value !== 'actions'
      )

  const fieldSelectLabels = createFieldSelectLabels(text.fieldSelect)

  const getPageCount = () => {
    return Math.ceil(inventoryQuantity / pageSize)
  }
  const handleShowError = () => {
    show({
      updatedSeverity: 'error',
      message: generalText.notificationText.error,
    })
  }

  const handleFiltersList = (
    newFiltersList: Filter[],
    newFilterInput: ListingFilterType
  ) => {
    setFiltersList(newFiltersList)
    setFiltersInput(newFilterInput)
    setPage(1)
  }

  const handleTabChange = (newValue: string | number) => {
    if (newValue === 1) history.replace({ ...location, hash: unpublishedHash })
    else history.replace({ ...location, hash: '#' })
    setPage(1)
  }

  const { loading: statusLoading, data: statusResponse } = useQuery<
    GenericData<ExtendedStatus[]>,
    FilterInputVariable
  >(GET_CAR_STATUSES, {
    variables: {
      input: {
        where: {
          slug_in: getStatusesIn,
        },
      },
    },
  })

  const { loading: carsLoading } = useQuery<ListInventory, FilterInputVariable>(
    LIST_INVENTORY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        input: {
          limit: pageSize,
          start: (page - 1) * pageSize,
          where: {
            text_search: searchValue,
            createdAt_gte: fromDate,
            createdAt_lte: toDate,
            status_in: getStatusesIn,
            status_nin: getStatusesNin,
            ...cleanFilters(formatFiltersWithCertification(filterInput)),
          },
          sort:
            sortModel.length > 0 && sortModel[0].sort
              ? {
                  [sortModel[0].field]: sortModel[0].sort,
                }
              : undefined,
        },
      },
      onCompleted(response) {
        const { data, count } = response.listInventoryCars
        setInventoryQuantity(count)
        setInventoryData(formatInventories(data))
      },
      onError() {
        handleShowError()
      },
    }
  )

  const [
    getCurboSpots,
    { loading: curboSpotsLoading, called: curboSpotsCalled },
  ] = useLazyQuery<GenericData<CurboSpot[]>>(GET_PUBLISHED_CURBO_SPOTS, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      setCurboSpots(response.data)
    },
  })

  const [getColors, { loading: colorsLoading, called: colorsCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_COLORS, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setColorsList(response.data)
        }
      },
    })

  const [getModels, { loading: modelsLoading, called: modelsCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_MODELS, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setModelsList(response.data)
        }
      },
    })

  const [getBrands, { loading: brandsLoading, called: brandsCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_BRANDS, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setBrandsList(response.data)
        }
      },
    })

  const [
    getBodyStyles,
    { loading: bodyStylesLoading, called: bodyStylesCalled },
  ] = useLazyQuery<GenericData<FilteringOption[]>>(GET_BODY_STYLES, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      if (response.data) {
        setBodyStyleList(response.data)
      }
    },
  })

  const [
    getCountryVersions,
    { loading: countryVersionLoading, called: countryVersionCalled },
  ] = useLazyQuery<GenericData<FilteringOption[]>>(GET_COUNTRY_VERSIONS, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      if (response.data) {
        setCountryVersionList(response.data)
      }
    },
  })
  const [getFuelTypes, { loading: fuelTypesLoading, called: fuelTypesCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_FUEL_TYPES, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setFuelTypeList(response.data)
        }
      },
    })
  const [
    getTransmissions,
    { loading: transmissionsLoading, called: transmissionsCalled },
  ] = useLazyQuery<GenericData<FilteringOption[]>>(GET_TRANSMISSIONS, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      if (response.data) {
        setTransmissionList(response.data)
      }
    },
  })
  const [
    getDriveTrains,
    { loading: driveTrainsLoading, called: driveTrainsCalled },
  ] = useLazyQuery<GenericData<FilteringOption[]>>(GET_DRIVE_TRAINS, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      if (response.data) {
        setDriveTrainList(response.data)
      }
    },
  })
  const [getDealers, { loading: dealersLoading, called: dealersCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_DEALERS, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setDealerList(response.data)
        }
      },
    })
  const [
    getCategories,
    { loading: categoriesLoading, called: categoriesCalled },
  ] = useLazyQuery<GenericData<FilteringOption[]>>(GET_CATEGORIES, {
    variables: {
      input: {
        sort: {
          name: 'asc',
        },
      },
    },
    onCompleted(response) {
      if (response.data) {
        setCategoryList(response.data)
      }
    },
  })
  const [getFeatures, { loading: featuresLoading, called: featuresCalled }] =
    useLazyQuery<GenericData<FilteringOption[]>>(GET_FEATURES, {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        if (response.data) {
          setFeaturesList(response.data)
        }
      },
    })

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setSearchInput(event.target.value)
  }

  const handleEditInventory = React.useCallback(
    (id: GridCellValue) => {
      history.push(buildDetailRoute(id, routes.INVENTORY_DETAIL))
    },
    [history]
  )

  const createColumns = useCallback(
    (statuses?: ExtendedStatus[], fields?: string[]) => {
      const hiddenField = (field: string) => {
        return fields && !fields.includes(field)
      }
      const {
        id: idColumn,
        isoDate: dateColumn,
        picture: pictureColumn,
        status: statusColumn,
        standard: standardColumn,
        verticalActions: actionsColumn,
      } = commonListColumns(
        InventoryStatusMap,
        selectedLanguage,
        text,
        handleEditInventory,
        undefined,
        statuses
      )
      return [
        { ...idColumn, hide: hiddenField(idColumn.field) },
        {
          ...standardColumn,
          field: InventoryColumnField.SLUG,
          headerName: text.table.slug,
          hide: hiddenField('slug'),
          flex: 2,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => (
            <Tooltip>{params.row.slug}</Tooltip>
          ),
        },
        {
          ...pictureColumn,
          hide: hiddenField(pictureColumn.field),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Avatar
                image={params.row.picture}
                alt="C"
                height={88}
                width={88}
                style={{
                  width: '88px',
                  height: '88px',
                }}
                variant="rounded"
              />
            )
          },
        },
        {
          ...standardColumn,
          field: InventoryColumnField.VEHICLE,
          headerName: text.table.vehicle,
          hide: hiddenField(InventoryColumnField.VEHICLE),
          flex: 2,
          width: 200,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => (
            <BiggerFontTooltip text={params.row.vehicle} />
          ),
        },
        {
          ...standardColumn,
          field: InventoryColumnField.TRIM,
          headerName: text.table.trim,
          hide: hiddenField(InventoryColumnField.TRIM),
          flex: 2,
          renderCell: (params: GridRenderCellParams) => {
            return (
              <CarTrimPopover
                trim={params.row.trim as string}
                bodyStyle={params.row.trimBox.bodyStyle}
                category={params.row.trimBox.category}
                exteriorColor={params.row.trimBox.exteriorColor}
                interiorColor={params.row.trimBox.interiorColor}
                countryVersion={params.row.trimBox.countryVersion}
                fuelType={params.row.trimBox.fuelType}
                curboSpot={params.row.trimBox.curboSpot}
              />
            )
          },
        },
        {
          ...standardColumn,
          field: InventoryColumnField.PRICE,
          headerName: text.table.price,
          hide: hiddenField(InventoryColumnField.PRICE),
          flex: 1,
          renderCell: (params: GridRenderCellParams) => (
            <BiggerFontTooltip
              text={`${priceCurrency} ${addCommas(
                parseInt(params.row.price, 10)
              )}`}
            />
          ),
        },
        {
          ...standardColumn,
          field: InventoryColumnField.MILEAGE,
          headerName: text.table.mileage,
          hide: hiddenField(InventoryColumnField.MILEAGE),
          flex: 1,
          renderCell: (params: GridRenderCellParams) => (
            <BiggerFontTooltip
              text={`${addCommas(params.row.mileage)} ${text.miles}`}
            />
          ),
        },
        {
          ...dateColumn,
          hide: hiddenField(InventoryColumnField.CREATED_AT),
          field: InventoryColumnField.CREATED_AT,
          headerName: text.table.createdAt,
        },
        {
          ...standardColumn,
          field: InventoryColumnField.CERTIFIED,
          headerName: text.table.certified,
          hide: hiddenField(InventoryColumnField.CERTIFIED),
          flex: 1,
          renderCell: (params: GridRenderCellParams) => (
            <CertifiedTag certified={params.row.certified} />
          ),
        },
        {
          ...statusColumn,
          sortable: true,
          hide: hiddenField(statusColumn.field),
        },
        {
          ...actionsColumn,
          hide: !supervisor || hiddenField(actionsColumn.field),
        },
      ]
    },
    [selectedLanguage, text, handleEditInventory, supervisor, priceCurrency]
  )

  const [columns, setColumns] = useState<GridColDef[]>(
    createColumns(statusList)
  )

  const handleSelectItem = (valueInput: string) => {
    setSelectedFields((prevFields) => {
      if (prevFields.includes(valueInput)) {
        return prevFields.filter((field) => field !== valueInput)
      }
      return [...prevFields, valueInput]
    })
  }

  const handleResetDefault = () => {
    setSelectedFields(defaultFields)
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        const fieldExist = defaultFields.some(
          (defaultField) => column.field === defaultField
        )
        return { ...column, hide: !fieldExist }
      })
    )
  }
  const submitSearchValue = () => {
    setPage(1)
    const newValue = checkSearchEmptiness(searchInput)
    setSearchValue(newValue)
  }

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
  }

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangePageSize = (event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number)
  }

  const handleChangeDateRange = (newDateRange: CalendarRangeType) => {
    setDateRange(newDateRange)
    setPage(1)
  }

  const createCar = () => {
    history.push(routes.INVENTORY_CREATION)
  }

  const handleFieldEntrySelect = (field: string) => {
    switch (field) {
      case 'curboSpot':
        if (!curboSpotsCalled) {
          getCurboSpots()
        }
        break
      case 'brand':
        if (!brandsCalled) {
          getBrands()
        }
        break
      case 'carModel':
        if (!modelsCalled) {
          getModels()
        }
        break
      case 'exteriorColor':
        if (!colorsCalled) {
          getColors()
        }
        break
      case 'bodyStyle':
        if (!bodyStylesCalled) {
          getBodyStyles()
        }
        break
      case 'countryVersion':
        if (!countryVersionCalled) {
          getCountryVersions()
        }
        break
      case 'fuelType':
        if (!fuelTypesCalled) {
          getFuelTypes()
        }
        break
      case 'transmission':
        if (!transmissionsCalled) {
          getTransmissions()
        }
        break
      case 'driveTrain':
        if (!driveTrainsCalled) {
          getDriveTrains()
        }
        break
      case 'dealer':
        if (!dealersCalled) {
          getDealers()
        }
        break
      case 'categories':
        if (!categoriesCalled) {
          getCategories()
        }
        break
      case 'features':
        if (!featuresCalled) {
          getFeatures()
        }
        break
      default:
        break
    }
  }

  const filtersLoading =
    curboSpotsLoading ||
    brandsLoading ||
    modelsLoading ||
    colorsLoading ||
    bodyStylesLoading ||
    countryVersionLoading ||
    fuelTypesLoading ||
    transmissionsLoading ||
    driveTrainsLoading ||
    dealersLoading ||
    categoriesLoading ||
    featuresLoading

  useEffect(() => {
    if (statusResponse?.data) {
      const formattedStatuses = statusResponse.data.map((status) => ({
        ...status,
        slug: status.slug.toLowerCase().replace('_', '-'),
      }))
      // Set columns but only change status header
      setColumns(createColumns(formattedStatuses, selectedFields))
      setStatusList(formattedStatuses)
    }
  }, [statusResponse, createColumns, selectedFields])

  useEffect(() => {
    if (hash && hash === unpublishedHash) {
      setTabValue(1)
    } else {
      setTabValue(0)
    }
  }, [hash])

  return (
    <Box width="100%">
      <Title
        title={text.title}
        subtitle={`${inventoryQuantity} ${text.description}`}
        icon={<PlaylistAddCheckIcon />}
        handleTabChange={handleTabChange}
        tabs={tabs}
        tabValue={tabValue}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent={admin ? 'space-between' : 'flex-end'}
        marginTop="2rem"
        width="100%"
      >
        {admin ? (
          <Button
            startIcon={<AddIcon />}
            size="medium"
            onClick={createCar}
            data-cy={cypressAddButton}
          >
            {text.newVehicleButton}
          </Button>
        ) : null}
        <Box display="flex">
          <Box marginRight="1rem" width="450px">
            <StyledTextField
              placeholder={text.searchPlaceholder}
              fullWidth
              value={searchInput}
              onChange={handleSearchChange}
              submitFunction={submitSearchValue}
              name="search"
            />
          </Box>
          <Box marginRight="1rem">
            <FilterByCriteria
              filtersList={filtersList}
              handleFiltersList={handleFiltersList}
              filterTypes={{
                year: 'number',
              }}
              file={textFiles.INVENTORY_LISTING}
              filterInput={filterInput}
              curboSpots={curboSpots}
              staticFields={inventoryStaticFields}
              handleFieldEntrySelect={handleFieldEntrySelect}
              loadingSelect={filtersLoading}
              options={{
                exteriorColor: colorsList,
                carModel: modelsList,
                brand: brandsList,
                bodyStyle: bodyStyleList,
                countryVersion: countryVersionList,
                fuelType: fuelTypeList,
                transmission: transmissionList,
                driveTrain: driveTrainList,
                dealer: dealerList,
                categories: categoryList,
                features: featuresList,
                certified: vehicleTypes,
                provider: providersList,
              }}
              // Still not working, no status shown in backend
              statusList={statusList}
            />
          </Box>
          <Box marginRight="1rem">
            <FieldFilter
              handleSelectItem={handleSelectItem}
              items={fieldSelectItems}
              selectedValues={selectedFields}
              handleResetDefault={handleResetDefault}
            />
          </Box>
          <Box>
            <DateRangeFilter
              dateRange={dateRange}
              handleChangeDateRange={handleChangeDateRange}
              title={text.dateRangeTitle}
              maxDate={null}
            />
          </Box>
        </Box>
      </Box>
      <Table
        searchValue={searchValue}
        dateRange={dateRange}
        rowHeight={110}
        columns={columns}
        data={inventoryData}
        currentPage={page}
        onPageChange={handleChangePage}
        onSelectChange={handleChangePageSize}
        pageSize={pageSize}
        selectItems={selectItems}
        pageCount={getPageCount()}
        loading={carsLoading || statusLoading}
        route={routes.INVENTORY_DETAIL}
        filtersList={filtersList}
        deletedItemId={deleteItem}
        setDeleteItemId={setDeleteItem}
        fields={selectedFields}
        fieldLabels={fieldSelectLabels}
        file={textFiles.INVENTORY_LISTING}
        filtersFile={textFiles.INVENTORY_LISTING}
        sortModel={sortModel}
        handleSortModelChange={handleSortModelChange}
        hideDownloadModal
        checkboxSelection={false}
        // checkboxSelection={supervisor}
        urlState={{ comesFromInventoryListingPage: true }}
      />
    </Box>
  )
}

export default InventoryListingPage
